import L from "leaflet";
import {NavLeafCanvas} from "../base/nav-leaf-canvas";
import '../styles/route-styles.css'
import "../extensions/leaflet-search/leaflet-search.min.js"
import "../extensions/leaflet-search/leaflet-search.min.css"
import "leaflet-polylinedecorator";
import '@fortawesome/fontawesome-free/js/fontawesome'
import '@fortawesome/fontawesome-free/js/regular'
import {eventBus} from "@/main";
import {useRoutesStore} from "@/stores/routes";
import { offsetGeneralRoute } from "@/utils/general-route"
import { ContextMenu, ContextMenuItem } from "@/utils/context-menu";
import { GeoUtils } from "@/utils/geo-utils";
import { WaypointUtils } from "@/utils/waypoint-utils";
import { sleep, debounce } from "@/utils/utils";

function clone(source) {
    if (source === undefined || source === null) return source;
    return JSON.parse(JSON.stringify(source));
}
export class EditorLeaf extends NavLeafCanvas {
    drawControl = null;
    editorPatchesGroup = null;
    editorDecoratorsGroup = null;
    editorTempGroup = null
    editorLinksGroup = null
    editorMarkersGroup = null
    drawingMode = false
    currentPoints = []
    tempLines = [];
    tempMarker = null;
    editorAreasGroup = null;

    constructor() {
        super();
    }

    async init(el) {
        super.init(el);

        this.editorTempGroup = L.layerGroup().addTo(this.map);
        this.editorPatchesGroup = L.layerGroup().addTo(this.map);
        this.editorDecoratorsGroup = L.layerGroup().addTo(this.map);
        this.editorLinksGroup = L.layerGroup().addTo(this.map);
        this.editorMarkersGroup = L.layerGroup().addTo(this.map);
        this.editorAreasGroup = L.featureGroup().addTo(this.map);
        
        this.renderPatchDecoratorsDebounced = debounce(
            () => {
                try {
                    this.renderPatchDecorators();
                }
                catch(e) {
                    console.error(e);
                    // for whatever reasons, first call always fails with Vue maximum stack exceptions
                    // second and subsequent calls are successfull
                    this.renderPatchDecorators();
                }
            }, 
            300
        );

        this.addDrawControl();
        this.createLengthDisplay();
        this.setupMapEvents();
        this.showRoute()
        this.showGeneralRoute();
        this.loadDataAsync().then(() => {
            this.renderPatchDecoratorsDebounced();
        });
    }

    setupMapEvents() {
        this.map.on('click', (e) => {
            if (!this.drawingMode) return;

            if (this.drawingMode === 'patch') {
                this.addPoint(e.latlng);
            } else if (this.drawingMode === 'link') {
                this.addLinkPoint(e.latlng);
            }
        });

        this.map.on('contextmenu', (e) => {
            if (this.drawingMode === 'patch' && this.currentPoints.length > 1) {
                this.finishDrawing();

            } else if (this.drawingMode === 'link' && this.currentPoints.length > 1) {
                this.finishLinkDrawing();
            } else if (!this.drawingMode) {
                this.checkLineClick(e);
            }
        });

        this.map.on('mousemove', (e) => {
            if (this.patchPointMove) {
                this.updatePatchPointMoveIndicator(this.patchPointMove.patch, this.patchPointMove.pointIndex, e.latlng);
                L.DomEvent.stop(e);
                return;
            }

            if (this.linkPointMove) {
                this.updatelinkPointMoveIndicator(this.linkPointMove.link, this.linkPointMove.pointIndex, e.latlng);
                L.DomEvent.stop(e);
                return;
            }

            if (!this.drawingMode || this.currentPoints.length === 0) return;

            if (this.drawingMode === 'patch') {
                this.updateTempLine(e.latlng);
            } else if (this.drawingMode === 'link') {
                this.updateTempLinkLine(e.latlng);
            }
        });

        this.map.on('mouseup', (e) => {
            if (this.patchPointMove) {
                this.patchPointMove.newPoint = { lat: e.latlng.lat, lon: e.latlng.lng };
                this.updatePatchPointCoordinates(
                    this.patchPointMove.patchId,
                    this.patchPointMove.pointIndex,
                    e.latlng
                );
                this.patchPointMove = null;
                this.updatePatchPointMoveIndicator();
                this.map.dragging.enable();

                L.DomEvent.stop(e);
            }

            if (this.linkPointMove) {
                this.linkPointMove.newPoint = { lat: e.latlng.lat, lon: e.latlng.lng };
                this.updateLinePointCoordinates(
                    this.linkPointMove.linkId,
                    this.linkPointMove.pointIndex,
                    e.latlng
                );

                this.linkPointMove = null;
                this.updatelinkPointMoveIndicator();
                this.map.dragging.enable();
                L.DomEvent.stop(e);           
            }
        })

        this.map.on('moveend', () => {
            this.renderPatchDecoratorsDebounced();
        })

        this.map.on('zoomend', () => {
            this.renderPatchDecoratorsDebounced();
        }) 
    }

    updateLinePointCoordinates(linkId, pointIndex, latLng) {
        const link = this.editorStore.getLinks.find(p => p.id === linkId);
        if (!link) return;
        const currentPoint = link.points[pointIndex];
        if (!currentPoint) return;

        const undoLink = clone(link)
        this.undoAction(
            () => {
                const storeLink = this.editorStore.getLinks.find(l => l.id === linkId);
                storeLink.points = undoLink.points;
                this.updateLinkLayers(storeLink);            
            }
        );

        const hemiPoint = GeoUtils.toPointHemisphere(link.points, latLng);
        link.points[pointIndex] = hemiPoint;
        this.updateLinkLayers(link);
    }

    updatePatchPointCoordinates(patchId, pointIndex, latLng) {
        const patch = this.editorStore.getPatches.find(p => p.id === patchId);
        if (!patch) return;

        const currentPoint = patch.points[pointIndex];
        if (!currentPoint) return;

        const undoPatch = clone(patch);
        const undoLinks = [];

        const links = this.editorStore.getLinks;
        if (pointIndex == 0) {
            // update all incoming links
            const incomingLinks = links.filter(l => {
                const lastPoint = l.points[l.points.length - 1];
                const distance = this.calculateDistance(
                    currentPoint.lat,
                    currentPoint.lon,
                    lastPoint.lat,
                    lastPoint.lon
                );

                return distance < 1;
            });

            incomingLinks.forEach(l => {
                undoLinks.push(clone(l));
                const lastPoint = l.points[l.points.length - 1];
                const hemiPoint = GeoUtils.toPointHemisphere(l.points, latLng);
                lastPoint.lat = hemiPoint.lat;
                lastPoint.lon = hemiPoint.lon;
                this.updateLinkLayers(l);
            });
        }

        if (pointIndex == patch.points.length - 1) {
            // update all outgoing links
            const outgoingLinks = links.filter(l => {
                const firstPoint = l.points[0];
                const distance = this.calculateDistance(
                    currentPoint.lat,
                    currentPoint.lon,
                    firstPoint.lat,
                    firstPoint.lon
                );

                return distance < 1;
            });

            outgoingLinks.forEach(l => {
                undoLinks.push(clone(l));
                const lastPoint = l.points[0];
                const hemiPoint = GeoUtils.toPointHemisphere(l.points, latLng);
                lastPoint.lat = hemiPoint.lat;
                lastPoint.lon = hemiPoint.lon;
                this.updateLinkLayers(l);
            });
        }

        const hemiPoint = GeoUtils.toPointHemisphere(patch.points, latLng);
        patch.points[pointIndex] = hemiPoint;        

        this.updatePatchLayers(patch);

        this.undoAction(
            () => {
                undoLinks.forEach(link => {
                    const storeLink = this.editorStore.getLinks.find(l => l.id === link.id);
                    storeLink.points = link.points;
                    this.updateLinkLayers(storeLink);
                })

                const storePatch = this.editorStore.getPatches.find(l => l.id === undoPatch.id);
                storePatch.points = undoPatch.points;
                this.updatePatchLayers(storePatch);
            }
        );
    }

    offsetCurrentPoints(offset) {
        this.currentPoints = this.offsetPoints(this.currentPoints, [offset])[0];
    }

    showGeneralRoute() {
        const routesStore = useRoutesStore();

        if (!routesStore.generalRoute || !routesStore.generalRoute.data) return;
        const generalRoute = routesStore.generalRoute.data;
        if (this.previewLayerGroup) {
            this.map.removeLayer(this.previewLayerGroup);
        }

        this.previewLayerGroup = L.layerGroup().addTo(this.map);
        this.previewStartPoint = generalRoute.startPoint;
        this.previewFinishPoint = generalRoute.finishPoint;

        offsetGeneralRoute(generalRoute).map((data, offsetIndex) => {           
            let mainRoutePolylines = [];
            if (data.lines && data.lines.length > 0) {
                data.lines.forEach(line => {
                    if (line.points && line.points.length > 0) {
                        const polyline = L.polyline(
                            line.points.map(p => [p.lat, p.lon]),
                            {color: '#000000', weight: 3, opacity: 0.8}
                        ).addTo(this.previewLayerGroup);

                        mainRoutePolylines.push(polyline);

                        line.points.forEach(point => {
                            const triangleIcon = L.divIcon({
                                className: 'triangle-icon',
                                html: '<div style="width: 0; height: 0; border-left: 6px solid transparent; border-right: 6px solid transparent; border-bottom: 10px solid #000000;"></div>',
                                iconSize: [10, 10],
                                iconAnchor: [5, 5]
                            });

                            L.marker([point.lat, point.lon], {icon: triangleIcon}).addTo(this.previewLayerGroup);
                        });
                    }
                });
            }

            if (data.links && data.links.length > 0) {
                data.links.forEach(link => {
                    if (link.points && link.points.length > 0) {
                        const polyline = L.polyline(
                            link.points.map(p => [p.lat, p.lon]),
                            {color: '#0000ff', weight: 3, opacity: 0.8}
                        ).addTo(this.previewLayerGroup);

                        mainRoutePolylines.push(polyline);
                        link.points.forEach(point => {
                            const triangleIcon = L.divIcon({
                                className: 'triangle-icon',
                                html: '<div style="width: 0; height: 0; border-left: 6px solid transparent; border-right: 6px solid transparent; border-bottom: 10px solid #0000ff;"></div>',
                                iconSize: [10, 10],
                                iconAnchor: [5, 5]
                            });

                            L.marker([point.lat, point.lon], {icon: triangleIcon}).addTo(this.previewLayerGroup);
                        });
                    }
                });
            }

            if (data.distance && mainRoutePolylines.length > 0) {
                const distanceKm = (data.distance / 1000).toFixed(1);
                mainRoutePolylines.forEach(polyline => {
                    polyline.bindTooltip(`Distance: ${distanceKm} km`, {
                        sticky: true,
                        direction: 'center'
                    });
                });
            }

            if (data.startPoint) {
                const startIcon = L.divIcon({
                    className: 'start-icon',
                    html: '<div style="width: 14px; height: 14px; background-color: #00a651; border-radius: 50%; border: 2px solid white;"></div>',
                    iconSize: [14, 14],
                    iconAnchor: [7, 7]
                });

                L.marker([data.startPoint.lat, data.startPoint.lon], {icon: startIcon})
                    .bindTooltip("Start: " + (data.startNode?.nodeId || '') + "/" + (data.startNode?.pointIndex || ''), {
                        permanent: true,
                        direction: 'top',
                        offset: L.point(0, -10)
                    }).addTo(this.previewLayerGroup);
            }

            if (data.finishPoint) {
                const finishIcon = L.divIcon({
                    className: 'finish-icon',
                    html: '<div style="width: 14px; height: 14px; background-color: #ff0000; border-radius: 50%; border: 2px solid white;"></div>',
                    iconSize: [14, 14],
                    iconAnchor: [7, 7]
                });

                L.marker([data.finishPoint.lat, data.finishPoint.lon], {icon: finishIcon})
                    .bindTooltip("Finish: " + (data.finishNode?.nodeId || '') + "/" + (data.finishNode?.pointIndex || ''), {
                        permanent: true,
                        direction: 'top',
                        offset: L.point(0, -10)
                    }).addTo(this.previewLayerGroup);
            }

            if (data.entryPoints && data.entryPoints.length > 0) {
                data.entryPoints.forEach((point, index) => {
                    const entryIcon = L.divIcon({
                        className: 'entry-icon',
                        html: '<div style="width: 0; height: 0; border-left: 6px solid transparent; border-right: 6px solid transparent; border-bottom: 10px solid #16ba16;"></div>',
                        iconSize: [10, 10],
                        iconAnchor: [5, 5]
                    });

                    L.marker([point.lat, point.lon], {icon: entryIcon})
                        .bindTooltip(`Entry ${index + 1}`, {
                            permanent: false,
                            direction: 'top'
                        }).addTo(this.previewLayerGroup);
                });
            }

            if (data.exitPoints && data.exitPoints.length > 0) {
                data.exitPoints.forEach((point, index) => {
                    const exitIcon = L.divIcon({
                        className: 'exit-icon',
                        html: '<div style="width: 0; height: 0; border-left: 6px solid transparent; border-right: 6px solid transparent; border-bottom: 10px solid #ff0000;"></div>',
                        iconSize: [10, 10],
                        iconAnchor: [5, 5]
                    });

                    L.marker([point.lat, point.lon], {icon: exitIcon})
                        .bindTooltip(`Exit ${index + 1}`, {
                            permanent: false,
                            direction: 'top'
                        }).addTo(this.previewLayerGroup);
                });
            }

            if (data.alternativeRoutes && data.alternativeRoutes.length > 0) {
                const altColors = ['#ff7f00', '#984ea3', '#4daf4a', '#e41a1c', '#377eb8'];

                data.alternativeRoutes.forEach((route, index) => {
                    
                    // too many lines, editor gets stuck. Only draw for normalized coordinares (offsetIndex = 1)
                    if (offsetIndex != 1) return;

                    const routeColor = altColors[index % altColors.length];

                    if (route.links && route.links.length > 0) {
                        route.links.forEach(link => {
                            if (link.points && link.points.length > 0) {
                                L.polyline(link.points.map(p => [p.lat, p.lon]), {
                                    color: routeColor,
                                    weight: 2,
                                    opacity: 0.5
                                }).bindTooltip(`Alt ${index + 1}`, {
                                    sticky: true
                                }).addTo(this.previewLayerGroup);

                                link.points.forEach(point => {
                                    const triangleIcon = L.divIcon({
                                        className: 'alt-triangle-icon',
                                        html: `<div style="width: 0; height: 0; border-left: 5px solid transparent; border-right: 5px solid transparent; border-bottom: 8px solid ${routeColor};"></div>`,
                                        iconSize: [8, 8],
                                        iconAnchor: [4, 4]
                                    });

                                    L.marker([point.lat, point.lon], {icon: triangleIcon}).addTo(this.previewLayerGroup);
                                });
                            }
                        });
                    }

                    if (route.lines && route.lines.length > 0) {
                        route.lines.forEach(line => {
                            if (line.points && line.points.length > 0) {
                                L.polyline(line.points.map(p => [p.lat, p.lon]), {
                                    color: routeColor,
                                    weight: 2,
                                    opacity: 0.5
                                }).addTo(this.previewLayerGroup);

                                line.points.forEach(point => {
                                    const triangleIcon = L.divIcon({
                                        className: 'alt-triangle-icon',
                                        html: `<div style="width: 0; height: 0; border-left: 5px solid transparent; border-right: 5px solid transparent; border-bottom: 8px solid ${routeColor};"></div>`,
                                        iconSize: [8, 8],
                                        iconAnchor: [4, 4]
                                    });

                                    L.marker([point.lat, point.lon], {icon: triangleIcon}).addTo(this.previewLayerGroup);
                                });
                            }
                        });
                    }
                });
            }

            if (offsetIndex == 1) {
                let allPoints = [];
                if (data.startPoint) allPoints.push([data.startPoint.lat, data.startPoint.lon]);
                if (data.finishPoint) allPoints.push([data.finishPoint.lat, data.finishPoint.lon]);

                if (data.lines && data.lines.length > 0) {
                    data.lines.forEach(line => {
                        if (line.points && line.points.length > 0) {
                            allPoints.push(...line.points.map(p => [p.lat, p.lon]));
                        }
                    });
                }

                if (data.links && data.links.length > 0) {
                    data.links.forEach(link => {
                        if (link.points && link.points.length > 0) {
                            allPoints.push(...link.points.map(p => [p.lat, p.lon]));
                        }
                    });
                }

                if (data.entryPoints && data.entryPoints.length > 0) {
                    allPoints.push(...data.entryPoints.map(p => [p.lat, p.lon]));
                }
                if (data.exitPoints && data.exitPoints.length > 0) {
                    allPoints.push(...data.exitPoints.map(p => [p.lat, p.lon]));
                }

                if (allPoints.length > 0) {
                    let bounds = L.latLngBounds(allPoints);
                    let southWest = bounds.getSouthWest();
                    let northEast = bounds.getNorthEast();
                    if (GeoUtils.isCrossingDateLine(southWest, northEast)) {
                        northEast = L.latlng(northEast.lat, northEast.lng + 360);
                        bounds = L.latLngBounds(southWest, northEast);
                        console.log("New bounds", bounds);
                    }

                    this.map.fitBounds(bounds);
                }
            }
        });
    }

    showRoute() {
        let points = this.routesStore.getShipWaypoints;

        if (!points || !Array.isArray(points) || points.length === 0) {
            return;
        }

        this.offsetPoints(points).map(points => {
            points.forEach((point, index) => {

                let tooltip = 'WP' + index
                let isPermanent = false
                if (index === 0) {
                    tooltip = "<div style='color:#16ba16;'>Start</div>"
                    isPermanent = true
                } else if (index === points.length - 1) {
                    tooltip = "<div style='color:#eda405;'>Finish</div>"
                    isPermanent = true
                }

                let newMarker = new L.circleMarker([point.lat, point.lon], {
                    bubblingMouseEvents: false,
                    autoPan: true,
                    radius: 4,
                    weight: 15,
                    opacity: 0,
                    fillOpacity: 0.8,
                    color: '#ef6d6d',
                }).bindTooltip(tooltip, {permanent: true, direction: 'top', offset: L.point(0, -4)})
                    .addTo(this.pmPane);

            });

            const routeLines = WaypointUtils.drawSegments(
                points, 
                () => ({color: '#ef6d6d', weight: 3})
            );
            
            routeLines.forEach(l => l.addTo(this.pmPane));
            //L.polyline(points, {color: '#ef6d6d', weight: 3}).addTo(this.pmPane);
        });
        
        this.initTooltips()
    }

    addDrawControl() {
        const drawPatchButton = L.control({position: 'topleft'});
        drawPatchButton.onAdd = () => {
            const div = L.DomUtil.create('div', 'leaflet-control leaflet-bar');
            div.innerHTML = '<a href="#" id="draw-patch-btn" title="Draw Line" style="font-weight: bold;">📏</a>';

            div.onclick = (e) => {
                e.preventDefault();
                L.DomEvent.stopPropagation(e);
                this.toggleDrawingMode('patch');
            };
            return div;
        };
        drawPatchButton.addTo(this.map);

        const drawLinkButton = L.control({position: 'topleft'});
        drawLinkButton.onAdd = () => {
            const div = L.DomUtil.create('div', 'leaflet-control leaflet-bar');
            div.innerHTML = '<a href="#" id="draw-link-btn" title="Draw Link" style="font-weight: bold; color: blue;">🔗</a>';

            div.onclick = (e) => {
                e.preventDefault();
                L.DomEvent.stopPropagation(e);
                this.toggleDrawingMode('link');
            };
            return div;
        };
        drawLinkButton.addTo(this.map);      

        this.drawControl = new L.Control.Draw({
            // options for draw control
            draw: {
                polyline: false,
                circle: false,
                polygon: {shapeOptions: {color: '#f357a1', weight: 1}},
                marker: false,
                rectangle: false,
                circlemarker: false
            },
            edit: {
                featureGroup: this.editorAreasGroup,
                remove: true
            }
        });

        this.map.addControl(this.drawControl);
        this.map.on('draw:created', (e) => {
            let type = e.layerType, layer = e.layer;

            this.currentPoints = layer.getLatLngs()[0];
            if (!this.currentPoints || this.currentPoints.length < 3) return;

            const areaId = Date.now().toString();
            layer.options = {
                areaId: areaId
            }

            this.editorAreasGroup.addLayer(layer);

            console.log('draw:created', type, layer);
            this.promptAreaDetails(areaId);
        });

        this.map.on('draw:edited', (e) => {
            (e.layers.getLayers() || []).forEach(layer => {
                const existingArea = this.editorStore.getAreas.find(a => a.id == layer.options?.areaId);
                if (!existingArea) {
                    alert(`Area was not found ${layer.options?.areaId}`)
                    return;
                }
                const updatedPoints = (layer.getLatLngs()[0] || [])
                    .map(latLng => GeoUtils.toPoint(latLng))
                    .filter(l => l.lat !== undefined && l.lon !== undefined)
                    ;

                if (updatedPoints.length < 3) {
                    alert(`Edit is not an area`);
                    return;
                }

                const updatedArea = {
                    ...existingArea,
                    points: updatedPoints
                }
                this.editorStore.updateArea(updatedArea);
                this.updateAreaLayers(updatedArea);
            })
        })

        this.map.on('draw:deleted', (e) => {
            (e.layers.getLayers() || []).forEach(layer => {
                const existingArea = this.editorStore.getAreas.find(a => a.id == layer.options?.areaId);
                if (!existingArea) {
                    alert(`Area was not found ${layer.options?.areaId}`)
                    return;
                }

                this.deleteArea(existingArea.id);
            });
        })
    }

    toggleDrawingMode(mode = 'patch') {

        if (this.drawingMode === mode) {
            this.drawingMode = false;
            this.map.getContainer().style.cursor = '';
            this.hideLengthDisplay();
        } else {

            this.drawingMode = mode;
            this.map.getContainer().style.cursor = 'crosshair';

            if (mode === 'patch') {
                this.showLengthDisplay();
                this.updateLengthDisplay([]);
            } else {
                this.hideLengthDisplay();
            }
        }

        this.currentPoints = [];
        this.removeTempElements();

        this.updateDrawButtonState();
    }

    showLengthDisplay() {
        if (this.lengthDisplay) {
            this.lengthDisplay.style.display = 'block';
        }
    }

    hideLengthDisplay() {
        if (this.lengthDisplay) {
            this.lengthDisplay.style.display = 'none';
        }
    }

    calculatePathLength(points) {
        if (!points || points.length < 2) return 0;

        let totalDistance = 0;
        for (let i = 1; i < points.length; i++) {
            const prevPoint = points[i - 1];
            const currentPoint = points[i];

            const distance = this.calculateDistance(
                prevPoint.lat,
                prevPoint.lng || prevPoint.lon,
                currentPoint.lat,
                currentPoint.lng || currentPoint.lon
            );

            totalDistance += distance;
        }

        return (totalDistance / 1852).toFixed(2);
    }

    updateLengthDisplay(points, additionalPoint = null) {
        if (!this.lengthDisplay) return;

        let pointsToMeasure = [...points];
        if (additionalPoint && points.length > 0) {
            pointsToMeasure.push(additionalPoint);
        }

        const lengthNM = this.calculatePathLength(pointsToMeasure);
        this.lengthDisplay.innerHTML = `<span>Length: ${lengthNM} nm</span>`;
        this.lengthDisplay.style.display = 'block';
    }

    removeTempElements() {
        this.tempLines.forEach(l => this.map.removeLayer(l))
        this.tempLines = [];

        if (this.tempMarker) {
            this.map.removeLayer(this.tempMarker);
            this.tempMarker = null;
        }
    }

    updateDrawButtonState() {
        const patchButtonElement = document.getElementById('draw-patch-btn');
        const linkButtonElement = document.getElementById('draw-link-btn');
        const areaButtonElement = document.getElementById('draw-area-btn');

        if (patchButtonElement) {
            patchButtonElement.style.backgroundColor = this.drawingMode === 'patch' ? '#f4a0a0' : '#ffffff';
            patchButtonElement.style.color = this.drawingMode === 'patch' ? '#000' : '#000';
            patchButtonElement.title = this.drawingMode === 'patch' ? 'Cancel Drawing' : 'Draw Path';
        }

        if (linkButtonElement) {
            linkButtonElement.style.backgroundColor = this.drawingMode === 'link' ? '#a0a0f4' : '';
            linkButtonElement.style.color = this.drawingMode === 'link' ? '#000' : '';
            linkButtonElement.title = this.drawingMode === 'link' ? 'Cancel Drawing' : 'Draw Link';
        }

        if (areaButtonElement) {
            areaButtonElement.style.backgroundColor = this.drawingMode === 'area' ? '#a0f4a0' : '';
            areaButtonElement.style.color = this.drawingMode === 'area' ? '#000' : '';
            areaButtonElement.title = this.drawingMode === 'area' ? 'Cancel Drawing' : 'Draw Area';
        }
    }

    addPoint(latlng) {
        this.currentPoints.push(latlng);

        let marker;
        if (this.currentPoints.length === 1) {

            marker = L.circleMarker(latlng, {
                radius: 4,
                fillColor: "#ff0b0b",
                color: "#000000",
                weight: 1,
                opacity: 1,
                fillOpacity: 1
            });
        } else {

            marker = L.circleMarker(latlng, {
                radius: 4,
                fillColor: "#000",
                color: "#000",
                weight: 1,
                opacity: 1,
                fillOpacity: 1
            });
        }

        marker.addTo(this.editorTempGroup);

        if (this.currentPoints.length > 1) {
            this.tempLines.forEach(l => this.map.removeLayer(l));

            L.polyline(this.currentPoints, {
                color: 'red',
                weight: 3
            }).addTo(this.editorTempGroup);
        }

        this.updateLengthDisplay(this.currentPoints);
    }

    updatelinkPointMoveIndicator(line, pointIndex, latlng) {
        this.tempLines.forEach(l => this.map.removeLayer(l));
        this.tempLines = [];
        
        if (!this.linkPointMove) return;
        if (!line || !latlng) return;

        const hemiPoint = GeoUtils.toPointHemisphere(line.points, latlng);
        const points = [
            line.points[pointIndex - 1], 
            hemiPoint, 
            line.points[pointIndex + 1]
        ].filter(Boolean);

        const offsetPoints = this.offsetPoints(points);
        offsetPoints.forEach(points => {
            this.tempLines.push(
                L.polyline(points, {
                    color: 'blue',
                    weight: 3,
                    dashArray: '5, 10',
                    opacity: 0.6
                }).addTo(this.map)
            );
        });
    }

    updatePatchPointMoveIndicator(patch, pointIndex, latlng) {
        this.tempLines.forEach(l => this.map.removeLayer(l));
        this.tempLines = [];

        if (!this.patchPointMove) return;
        if (!patch || !latlng) return;

        const hemiPoint = GeoUtils.toPointHemisphere(patch.points, latlng);

        const points = [
            patch.points[pointIndex - 1], 
            hemiPoint, 
            patch.points[pointIndex + 1]
        ].filter(Boolean);
        
        const offsetPoints = this.offsetPoints(points);
        offsetPoints.forEach(points => {
            this.tempLines.push(
                L.polyline(points, {
                    color: 'black',
                    weight: 3,
                    dashArray: '5, 10',
                    opacity: 0.6
                }).addTo(this.map)
            );
        });    
    }

    updateTempLine(latlng) {
        this.tempLines.forEach(l => this.map.removeLayer(l));
        this.tempLines = [];

        if (this.currentPoints.length === 0) return;

        const points = [...this.currentPoints, latlng];

        const offsetPoints = this.offsetPoints(points);
        offsetPoints.forEach(points => {
            this.tempLines.push(
                L.polyline(points, {
                    color: 'black',
                    weight: 3,
                    dashArray: '5, 10',
                    opacity: 0.6
                }).addTo(this.map)
            );
        });

        this.updateLengthDisplay(this.currentPoints, latlng);
    }

    finishDrawing() {
        if (this.currentPoints.length < 2) return;

        this.removeTempElements();
        this.hideLengthDisplay();
        const lineId = Date.now().toString();
        this.promptLineDetails(lineId);
        this.drawingMode = false;
        this.map.getContainer().style.cursor = '';
        this.updateDrawButtonState();
    }

    addLinkPoint(latlng) {

        if (this.currentPoints.length === 0) {
            const nearestPoint = this.findNearestPatchEndpoint(latlng, 'end');
            if (nearestPoint) {
                latlng = nearestPoint;
            }
        }

        this.currentPoints.push(latlng);

        const marker = L.circleMarker(latlng, {
            radius: 4,
            fillColor: "#0000ff",
            color: "#0000ff",
            weight: 1,
            opacity: 1,
            fillOpacity: 1
        });

        marker.addTo(this.editorTempGroup);

        if (this.currentPoints.length > 1) {
            this.tempLines.forEach(l => this.map.removeLayer(l));
            this.tempLines = [];

            L.polyline(this.currentPoints, {
                color: 'green',
                weight: 3,
                dashArray: '5, 5'
            }).addTo(this.editorTempGroup);
        }
    }

    updateTempLinkLine(latlng) {
        this.map.removeLayer(this.editorTempGroup)
        this.tempLines.forEach(l => this.map.removeLayer(l));
        this.tempLines = [];

        if (this.currentPoints.length === 0) return;

        const points = [...this.currentPoints, latlng];
        const offsetPoints = this.offsetPoints(points);
        offsetPoints.forEach(points => {
            this.tempLines.push(
                L.polyline(points, {
                    color: 'blue',
                    weight: 3,
                    dashArray: '5, 5',
                    opacity: 0.6
                }).addTo(this.map)
            );
        });
    }

    finishLinkDrawing() {
        if (this.currentPoints.length < 2) return;

        const lastPointIndex = this.currentPoints.length - 1;
        const lastPoint = this.currentPoints[lastPointIndex];
        const nearestStartPoint = this.findNearestPatchEndpoint(lastPoint, 'start');

        if (nearestStartPoint) {

            this.currentPoints[lastPointIndex] = nearestStartPoint;

            this.editorMarkersGroup.eachLayer(layer => {
                const markerLatLng = layer.getLatLng();
                if (Math.abs(markerLatLng.lat - lastPoint.lat) < 0.000001 &&
                    Math.abs(markerLatLng.lng - (lastPoint.lng || lastPoint.lon)) < 0.000001) {

                    layer.setLatLng(nearestStartPoint);
                }
            });
        }

        this.removeTempElements();

        const linkId = Date.now().toString();

        const linkData = {
            id: linkId,
            name: 'Link ' + linkId.substring(linkId.length - 5),
            points: this.currentPoints.map(point => ({
                lat: point.lat,
                lon: point.lng || point.lon
            }))
        };

        this.editorStore.addLink(linkData);
        //this.renderLink(linkData);
        this.currentPoints = [];
        this.drawingMode = false;
        this.map.getContainer().style.cursor = '';

        this.clearTempMarkers();
        this.removeTempElements();
        this.editorTempGroup.clearLayers();
        this.updateDrawButtonState();

        const link = this.editorStore.getLinks.find(l => l.id === linkId);
        this.updateLinkLayers(link);
    }

    findNearestPatchEndpoint(latlng, pointType) {
        let nearestPoint = null;
        let minDistance = Infinity;

        const patches = this.editorStore.getPatches;
        const west = this.map.getBounds().getWest();
        const east = this.map.getBounds().getEast();

        patches.forEach(patch => {
            const offsetPoints = this.offsetPoints(patch.points);
            
            offsetPoints.forEach(points => {
                if (!points || points.length === 0) return;

                let refPoint;
                if (pointType === 'start') {
                    refPoint = points[0];
                } else if (pointType === 'end') {
                    refPoint = points[points.length - 1];
                }

                if (!refPoint) return;

                if (refPoint.lon < west || refPoint.lon > east) return;

                const distance = this.calculateDistance(
                    latlng.lat,
                    latlng.lng || latlng.lon,
                    refPoint.lat,
                    refPoint.lon
                );

                if (distance < minDistance) {
                    minDistance = distance;
                    nearestPoint = L.latLng(refPoint.lat, refPoint.lon);
                }
            });
        });

        return nearestPoint;
    }

    calculateDistance(lat1, lon1, lat2, lon2) {

        const R = 6371000;
        const dLat = this.toRadians(lat2 - lat1);
        const dLon = this.toRadians(lon2 - lon1);

        const a =
            Math.sin(dLat / 2) * Math.sin(dLat / 2) +
            Math.cos(this.toRadians(lat1)) * Math.cos(this.toRadians(lat2)) *
            Math.sin(dLon / 2) * Math.sin(dLon / 2);

        const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));

        return R * c;
    }

    toRadians(degrees) {
        return degrees * Math.PI / 180;
    }

    createLengthDisplay() {

        this.lengthControl = L.control({position: 'bottomleft'});
        this.lengthControl.onAdd = () => {
            const div = L.DomUtil.create('div', 'leaflet-control leaflet-bar leaflet-length-display');
            div.id = 'path-length-display';
            div.innerHTML = '<span>Length: 0 nm</span>';
            div.style.backgroundColor = 'white';
            div.style.padding = '5px 10px';
            div.style.border = '1px solid #ccc';
            div.style.display = 'none';
            return div;
        };
        this.lengthControl.addTo(this.map);
        this.lengthDisplay = document.getElementById('path-length-display');
    }

    offsetPoints(points, offsets) {
        if (!points) return points;

        if (!offsets)
            offsets = GeoUtils.defaultOffsets;

        return offsets.map(offset => {
            return points.map(p => {
                if (p.lng) {
                    return L.latLng(p.lat, p.lng + offset);
                }
                else {
                    return {
                        ...p,
                        lon: p.lon + offset
                    }
                }
            })
        });
    }

    renderLink(link) {
        if (!link.points || link.points.length < 2) return;

        const hasLinkFilter = [
            this.editorStore.showOutgoingLinksForPatches != "",
            this.editorStore.showOutgoingLinksForPatches != ""
        ].some(Boolean);

        let isIncomingLink = !hasLinkFilter;
        let isOutgoingLink = !hasLinkFilter;

        if (this.editorStore.showOutgoingLinksForPatches != "")
        {
            const fromPatches = this
                .editorStore
                .showOutgoingLinksForPatches
                .split(",")
                .map(s => s.trim())
                .filter(s => s !== "")
                ;

            if (fromPatches.length) {
                const patches = this.editorStore.getPatches;
                isOutgoingLink = fromPatches.some(p => {
                    const sourcePath = patches.find(patch => patch.id === p || patch.name === p );
                    if (!sourcePath) return false;

                    const lastPoint = sourcePath.points[sourcePath.points.length - 1];
                    const firstLinkPoint = link.points[0];

                    const dx = Math.abs(lastPoint.lat - firstLinkPoint.lat);
                    const dy = Math.abs(lastPoint.lon - firstLinkPoint.lon);
                    return dx < 0.000001 && dy < 0.000001;
                });
            }
        }

        if (this.editorStore.showIncomingLinksForPatches != "")
        {
            const toPatches = this
                .editorStore
                .showIncomingLinksForPatches
                .split(",")
                .map(s => s.trim())
                .filter(s => s !== "")
                ;

            if (toPatches.length) {
                const patches = this.editorStore.getPatches;
                isIncomingLink = toPatches.some(p => {
                    const sourcePath = patches.find(patch => patch.id === p || patch.name === p );
                    if (!sourcePath) return false;

                    const firstPoint = sourcePath.points[0];
                    const lastLinkPoint = link.points[link.points.length - 1];

                    const dx = Math.abs(firstPoint.lat - lastLinkPoint.lat);
                    const dy = Math.abs(firstPoint.lon - lastLinkPoint.lon);
                    return dx < 0.000001 && dy < 0.000001;
                });
            }
        }

        const filterResults = [isIncomingLink, isOutgoingLink];
        if (filterResults.every(v => !v)) return;

        const offsetPoints = this.offsetPoints(link.points);
        offsetPoints.forEach(points => {
            const linkLatLngs = points.map(point => L.latLng(point.lat, point.lon));
            const lengthNM = this.calculatePathLength(linkLatLngs);
            const isOceanLink = link.oceanLink === true;

            const isPortLoop = link.type === 'PortLoop';

            for (let i = 0; i < linkLatLngs.length - 1; i++) {
                const latLngs = [linkLatLngs[i], linkLatLngs[i + 1]];

                const polyline = L.polyline(latLngs, {
                    color: isPortLoop ? 'orange' : 'blue',
                    weight: 2,
                    dashArray: isOceanLink ? '15 5' : '5, 5',
                    opacity: 0.8,
                    linkId: link.id,
                    isPortLoop: isPortLoop,
                    isOceanLink: isOceanLink,
                    fromIndex: i,
                    toIndex: i + 1
                }).addTo(this.editorLinksGroup);

                const contextMenuOptions = {
                    isFirstSegment: i == 0,
                    isMidSegment: i != 0 && i != linkLatLngs.length - 2,
                    isLastSegment: i == linkLatLngs.length - 2,
                    fromIndex: i,
                    toIndex: i + 1
                }


                polyline.on('contextmenu', (e) => {
                    const linkId = e.target.options.linkId;
                    this.linkContextMenuHandler(
                        linkId, 
                        e.latlng,
                        e.originalEvent.clientX, 
                        e.originalEvent.clientY,
                        { 
                            isLink: true,
                            ...contextMenuOptions
                        }
                    );
                    L.DomEvent.stop(e);
                });

                polyline.on('mouseover', (e) => {
                    if (this.lengthDisplay) {
                        this.lengthDisplay.innerHTML = `<span>Link Length: ${lengthNM} nm, ID: ${link.id}</span>`;
                        this.lengthDisplay.style.display = 'block';
                    }
                });

                polyline.on('mouseout', (e) => {

                    if (this.lengthDisplay) {
                        this.lengthDisplay.style.display = 'none';
                    }
                });
            }

            // add point markers
            //this.addFromMarker(patchLatLngs[0], patch);

            for (let i = 1; i < linkLatLngs.length - 1; i++) {
                const pointMarker = L.circleMarker(linkLatLngs[i], {
                    radius: 3,
                    fillColor: 'blue',
                    color: 'blue',
                    weight: 1,
                    opacity: 1,
                    fillOpacity: 1,
                    linkId: link.id,
                    pointIndex: i
                }).addTo(this.editorMarkersGroup);

                pointMarker.on('contextmenu', (e) => {
                    const linkId = e.target.options.linkId;
                    this.linkContextMenuHandler(
                        linkId, 
                        e.latlng,
                        e.originalEvent.clientX, 
                        e.originalEvent.clientY,
                        { isMidPoint: true, pointIndex: i }
                    );
                    L.DomEvent.stop(e);
                });

                this.addlinkPointMoveHandler(link, pointMarker);
            }
           
            //this.addToMarker(patchLatLngs[patchLatLngs.length - 1], patch, patchLatLngs.length - 1);   
        })
    }

    toggleLinkPortLoop(linkId) {

        const link = this.editorStore.getLinks.find(l => l.id === linkId);
        if (!link) return;

        const isPortLoop = link.type === 'PortLoop';
        const newType = isPortLoop ? 'Common' : 'PortLoop';

        this.editorStore.updateLinkType(linkId, newType);
        this.updateLinkLayers(link);
    }

    undoAction(restore) {
        eventBus.$emit(
            'editor-add-undo-action', 
            {
                restore
            }
        )     
    }

    deleteLink(linkId) {
        const link = this.editorStore.getLinks.find(p => p.id === linkId);
        if (!link) return;

        this.undoAction(
            () => {
                this.editorStore.addLink(link);
                const storeLink = this.editorStore.getLinks.find(l => l.id === linkId);
                this.updateLinkLayers(storeLink);            
            }
        );

        this.removeLinkLayers(linkId);
        this.editorStore.removeLink(linkId);
    }

    //addUndoAction(patches, links) {
    //    if (!link) return;
    //    const undoLink = clone(link)
    //    this.undoAction(
    //        () => {
    //            const storeLink = this.editorStore.getLinks.find(l => l.id === linkId);
    //            storeLink.points = undoLink.points;
    //            this.updateLinkLayers(storeLink);            
    //        }
    //    );        
    //}

    deletePointFromLink(linkId, pointIndex) {
        const link = this.editorStore.getLinks.find(p => p.id === linkId);
        if (!link) return;
        
        const undoLink = clone(link);
        this.undoAction(
            () => {
                const storeLink = this.editorStore.getLinks.find(l => l.id === linkId);
                storeLink.points = undoLink.points;
                this.updateLinkLayers(storeLink);            
            }
        );  

        const newPoints = [
            ...link.points.slice(0, pointIndex),
            ...link.points.slice(pointIndex + 1)            
        ];
        link.points = newPoints;

        this.updateLinkLayers(link);
    }

    deletePointFromPatch(patchId, pointIndex) {
        const patch = this.editorStore.getPatches.find(p => p.id === patchId);
        if (!patch) return;

        const undoPatch = clone(patch);
        this.undoAction(
            () => {
                const storePatch = this.editorStore.getPatches.find(l => l.id === undoPatch.id);
                storePatch.points = undoPatch.points;
                this.updatePatchLayers(storePatch);
            }
        );  

        const newPoints = [
            ...patch.points.slice(0, pointIndex),
            ...patch.points.slice(pointIndex + 1)            
        ];
        patch.points = newPoints;

        this.updatePatchLayers(patch);
    }

    splitPatch(lineId, fromIndex, toIndex) {
        if (!lineId ||
            fromIndex === undefined || fromIndex === null || fromIndex < 0 ||
            toIndex === undefined || toIndex === null || toIndex < 0
        ) return;

        const patch = this.editorStore.getPatches.find(p => p.id === lineId);
        if (!patch) return;

        const newPoints = [
            ...patch.points.slice(0, fromIndex + 1)
        ]

        const secondPatchPoints = [
            ...patch.points.slice(toIndex)
        ]

        patch.points = newPoints;
        this.updatePatchLayers(patch);  

        const newPatchId = Date.now().toString();
        const secondPatch = {
            ...patch,
            id: newPatchId,
            name: 'Path ' + newPatchId.substring(newPatchId.length - 5),
            points: secondPatchPoints
        }

        this.editorStore.addPatch(secondPatch);
        this.renderPatch(secondPatch);
    }
    
    insertPointToLink(linkId, fromIndex, toIndex, latLng) {
        if (!linkId || !latLng ||
            fromIndex === undefined || fromIndex === null || fromIndex < 0 ||
            toIndex === undefined || toIndex === null || toIndex < 0
        ) return;

        const link = this.editorStore.getLinks.find(p => p.id === linkId);
        if (!link) return;

        const hemiPoint = GeoUtils.toPointHemisphere(link.points, latLng);

        const newPoints = [
            ...link.points.slice(0, fromIndex + 1),
            hemiPoint,
            ...link.points.slice(toIndex)
        ]

        link.points = newPoints;

        this.updateLinkLayers(link);
    }
    
    insertPointToPatch(lineId, fromIndex, toIndex, latLng) {
        if (!lineId || !latLng ||
            fromIndex === undefined || fromIndex === null || fromIndex < 0 ||
            toIndex === undefined || toIndex === null || toIndex < 0
        ) return;

        const patch = this.editorStore.getPatches.find(p => p.id === lineId);
        if (!patch) return;

        const hemiPoint = GeoUtils.toPointHemisphere(patch.points, latLng);
        const newPoints = [
            ...patch.points.slice(0, fromIndex + 1),
            hemiPoint,
            ...patch.points.slice(toIndex)
        ]

        patch.points = newPoints;

        this.updatePatchLayers(patch);
    }

    updatePatchLayers(patch) {
        this.removePatchLayers(patch);
        this.renderPatch(patch);
        this.renderPatchDecorators({
            ids: [patch.id]
        })
    }

    updateLinkLayers(link) {
        this.removeLinkLayers(link?.id);
        this.renderLink(link);
    }

    updateAreaLayers(area) {
        this.removeAreaLayers(area);
        this.renderArea(area);
    }

    removeLinkLayers(linkId) {
        if (!linkId) return;

        this.editorLinksGroup.eachLayer(layer => {
            if ((layer.options && layer.options.linkId === linkId) ||
                layer.linkId === linkId) {
                this.editorLinksGroup.removeLayer(layer);
            }
        });

        this.editorMarkersGroup.eachLayer(layer => {
            if ((layer.options && layer.options.linkId === linkId) ||
                layer.linkId === linkId) {
                this.editorMarkersGroup.removeLayer(layer);
            }
        });
    }

    removePatchLayers(patch) {
        if (!patch) return;
        
        [this.editorPatchesGroup, this.editorMarkersGroup, this.editorDecoratorsGroup].forEach(group => {
            group.eachLayer(layer => {
                if (layer.options.patchId === patch?.id) {
                    group.removeLayer(layer);
                }
            });
        });
    }

    removeAreaLayers(area) {
        if (!area) return;
        
        [this.editorAreasGroup].forEach(group => {
            group.eachLayer(layer => {
                if (layer.options.areaId === area?.id) {
                    group.removeLayer(layer);
                }
            });
        });
    }

    promptLineDetails(lineId) {
        const patch = this.editorStore.getPatches.find(p => p.id === lineId);
        const isEdit = !!patch;

        const initialValues = patch 
        ? {
            name: patch.name,
            region: patch.region || "",
            depth: patch.depth,
            status: patch.status,
            type: patch.type,
            source: patch.source,
            vesselLength: patch.vesselLimits?.length,
            vesselBreadth: patch.vesselLimits?.breadth,
            vesselHeight: patch.vesselLimits?.height,
            vesselDraught: patch.vesselLimits?.draught,
            vesselTypes: (patch.vesselLimits?.types || []).join(", ")
        }
        : {
            name: 'Path ' + lineId.substring(lineId.length - 5),
            region: '',
            depth: 10,
            status: 'medium',
            type: 'coastal',
            source: 'map',
        };

        return new Promise((resolve, reject) => {

            const handleSubmit = (formData) => {
                eventBus.$off('line-details:submit', handleSubmit);
                eventBus.$off('line-details:cancel', handleCancel);

                const patchData = {
                    id: lineId,
                    name: formData.name,
                    region: formData.region,
                    depth: parseFloat(formData.depth) || 0,
                    status: formData.status,
                    type: formData.type,
                    source: formData.source,
                    port: false,
                    points: this.currentPoints.map(point => ({
                        lat: point.lat,
                        lon: point.lng
                    }))
                };

                const hasValue = (v) => v !== undefined && v !== null && v !== "" && v !== 0;
                const vesselSettings = {};

                if (hasValue(formData.vesselLength)) {
                    vesselSettings.length = parseFloat(formData.vesselLength) || 0
                }
                if (hasValue(formData.vesselBreadth)) {
                    vesselSettings.breadth = parseFloat(formData.vesselBreadth) || 0
                }
                if (hasValue(formData.vesselHeight)) {
                    vesselSettings.height = parseFloat(formData.vesselHeight) || 0
                }
                if (hasValue(formData.vesselDraught)) {
                    vesselSettings.draught = parseFloat(formData.vesselDraught) || 0
                }
                if (hasValue(formData.vesselTypes)) {
                    const vesselTypes = formData
                        .vesselTypes
                        .split(',')
                        .map(t => t.trim())
                        .filter(t => t && t !== "");
                    if (vesselTypes.length) {
                        vesselSettings.types = vesselTypes;
                    }
                }
                if (Object.keys(vesselSettings).length) {
                    patchData.vesselLimits = vesselSettings;
                }

                this.clearTempMarkers();
                const existingPatch = this.editorStore.getPatches.find(p => p.id === lineId);
                if (existingPatch) {
                    const updatedPatch = {
                        ...existingPatch,
                        ...patchData,
                        // keep some data unchanged
                        port: existingPatch.port,
                        points: existingPatch.points
                    }
                    this.editorStore.updatePatch(updatedPatch);
                }
                else {
                    this.editorStore.addPatch(patchData);
                }

                this.renderPatch(patchData);
                this.renderPatchDecorators({
                    ids: [patchData.id]
                })
                this.currentPoints = [];
                this.drawingMode = false;
                this.map.getContainer().style.cursor = '';

                resolve(patchData);
            };

            const handleCancel = () => {
                eventBus.$off('line-details:submit', handleSubmit);
                eventBus.$off('line-details:cancel', handleCancel);

                this.currentPoints = [];
                this.drawingMode = false;
                this.map.getContainer().style.cursor = '';

                // cleanup unfinished lines
                this.clearTempMarkers();
                this.removeTempElements();
                this.editorTempGroup.clearLayers();

                reject(new Error('User cancelled'));
            };

            eventBus.$on('line-details:submit', handleSubmit);
            eventBus.$on('line-details:cancel', handleCancel);

            eventBus.$emit('show-line-details-dialog', initialValues);
        }).catch(err => {

        });
    }

    promptAreaDetails(areaId) {
        const area = this.editorStore.getAreas.find(p => p.id === areaId);
        const isEdit = !!area;
        console.log("Edit", isEdit);

        const initialValues = area
        ? {
            name: area.name,
            depth: area.depth,
            vesselLength: area.vesselLimits?.length,
            vesselBreadth: area.vesselLimits?.breadth,
            vesselHeight: area.vesselLimits?.height,
            vesselDraught: area.vesselLimits?.draught,
            vesselTypes: (area.vesselLimits?.types || []).join(", ")
        }
        : {
            name: 'Area ' + areaId.substring(areaId.length - 5),
            depth: 5
        };

        return new Promise((resolve, reject) => {

            const handleSubmit = (formData) => {
                eventBus.$off('area-details:submit', handleSubmit);
                eventBus.$off('area-details:cancel', handleCancel);

                const areaData = {
                    id: areaId,
                    name: formData.name,
                    depth: parseFloat(formData.depth) || 0,
                    points: this.currentPoints.map(point => ({
                        lat: point.lat,
                        lon: point.lng
                    }))
                };

                const hasValue = (v) => v !== undefined && v !== null && v !== "" && v !== 0;
                const vesselSettings = {};

                if (hasValue(formData.vesselLength)) {
                    vesselSettings.length = parseFloat(formData.vesselLength) || 0
                }
                if (hasValue(formData.vesselBreadth)) {
                    vesselSettings.breadth = parseFloat(formData.vesselBreadth) || 0
                }
                if (hasValue(formData.vesselHeight)) {
                    vesselSettings.height = parseFloat(formData.vesselHeight) || 0
                }
                if (hasValue(formData.vesselDraught)) {
                    vesselSettings.draught = parseFloat(formData.vesselDraught) || 0
                }
                if (hasValue(formData.vesselTypes)) {
                    const vesselTypes = formData
                        .vesselTypes
                        .split(',')
                        .map(t => t.trim())
                        .filter(t => t && t !== "");
                    if (vesselTypes.length) {
                        vesselSettings.types = vesselTypes;
                    }
                }
                if (Object.keys(vesselSettings).length) {
                    areaData.vesselLimits = vesselSettings;
                }

                this.clearTempMarkers();
                const existingArea = this.editorStore.getAreas.find(p => p.id === areaId);
                if (existingArea) {
                    const updatedArea = {
                        ...existingArea,
                        ...areaData,
                        // keep some data unchanged
                        points: existingArea.points
                    }
                    this.editorStore.updateArea(updatedArea);
                    this.updateAreaLayers(updatedArea);
                }
                else {
                    this.editorStore.addArea(areaData);
                    this.renderArea(areaData);
                }

                this.currentPoints = [];
                this.drawingMode = false;
                this.map.getContainer().style.cursor = '';

                resolve(areaData);
            };

            const handleCancel = () => {
                eventBus.$off('area-details:submit', handleSubmit);
                eventBus.$off('area-details:cancel', handleCancel);

                this.currentPoints = [];
                this.drawingMode = false;
                this.map.getContainer().style.cursor = '';

                // cleanup unfinished lines
                this.clearTempMarkers();
                this.removeTempElements();
                this.editorTempGroup.clearLayers();

                reject(new Error('User cancelled'));
            };

            eventBus.$on('area-details:submit', handleSubmit);
            eventBus.$on('area-details:cancel', handleCancel);

            eventBus.$emit('show-area-details-dialog', initialValues);
        }).catch(err => {

        });
    }


    clearTempMarkers() {
        this.editorTempGroup.clearLayers();

        //this.editorMarkersGroup.eachLayer(layer => {
        //    if (!layer.options.patchId) {
        //        this.editorMarkersGroup.removeLayer(layer);
        //    }
        //});
    }

    async loadPatchesAsync() {
        const patches = this.editorStore.getPatches;
        for (let i = 0; i < patches.length; i++) {
            if (i && (i % 300) == 0) {
                await sleep(50);
                eventBus.$emit('editor-data-load-message', `Loaded ${i} of ${patches.length} patches`);
            }

            this.renderPatch(patches[i]);
        }
    }
    
    async loadLinksAsync() {
        const links = this.editorStore.getLinks;
        for (let i = 0; i < links.length; i++) {
            if (i && (i % 300) == 0) {
                await sleep(50);
                eventBus.$emit('editor-data-load-message', `Loaded ${i} of ${links.length} links`); 
            }

            this.renderLink(links[i]);
        }
    }

    loadAreas() {
        const areas = this.editorStore.getAreas;
        areas.forEach(area => this.renderArea(area));
    }

    updatePatchStyle(patchId) {
        const patch = this.editorStore.getPatches.find(p => p.id === patchId);
        if (!patch) return;
        const isJunction = patch.isJunction;
        const color = isJunction ? 'red' : patch.port ? 'green' : 'black';
        const dashArray = patch.isVirtual ? '10, 10' : null;

        this.editorPatchesGroup.eachLayer(layer => {
            if ((layer.options && layer.options.patchId === patchId) &&
                (layer instanceof L.Polyline) &&
                !(layer instanceof L.PolylineDecorator)) {
                layer.setStyle({
                    color,
                    dashArray
                });
            }
        }); 
    }

    toggleOceanLink(linkId) {
        const link = this.editorStore.getLinks.find(p => p.id === linkId);
        if (!link) return;
        
        link.oceanLink = link.oceanLink !== true;
        this.updateLinkLayers(link);
    }

    togglePatchPortAttribute(patchId) {

        const patch = this.editorStore.getPatches.find(p => p.id === patchId);
        if (!patch) return;

        const newPortValue = patch.port !== true;
        this.editorStore.updatePatchPort(patchId, newPortValue);
        const newColor = newPortValue ? 'green' : 'black';

        this.editorPatchesGroup.eachLayer(layer => {
            if ((layer.options && layer.options.patchId === patchId) &&
                (layer instanceof L.Polyline) &&
                !(layer instanceof L.PolylineDecorator)) {
                layer.setStyle({color: newColor});
            }
        });

        this.editorDecoratorsGroup.eachLayer(layer => {
            if ((layer.options && layer.options.patchId === patchId) &&
                (layer instanceof L.PolylineDecorator ||
                    (layer.options.patterns && layer.options.patterns[0] && layer.options.patterns[0].symbol))) {

                this.editorDecoratorsGroup.removeLayer(layer);
            }
        });

        this.editorPatchesGroup.eachLayer(layer => {
            if ((layer.options && layer.options.patchId === patchId) &&
                (layer instanceof L.Polyline) &&
                !(layer instanceof L.PolylineDecorator)) {
                const decorator = L.polylineDecorator(layer, {
                    patterns: [
                        {
                            offset: '25%',
                            repeat: '50%',
                            symbol: L.Symbol.arrowHead({
                                pixelSize: 12,
                                polygon: true,
                                pathOptions: {
                                    fillOpacity: 1,
                                    weight: 0,
                                    color: newColor
                                }
                            })
                        }
                    ]
                }).addTo(this.editorDecoratorsGroup);
                decorator.options.patchId = patchId;
            }
        });
    }

    linkContextMenuHandler(linkId, latLng, x, y, config) {
        const {
            isLink = false,
            isFirstSegment = false,
            isMidSegment = false,
            isLastSegment = false,
            isFirstPoint = false,
            isLastPoint = false,
            isMidPoint = false,
            isDecorator = false,
            fromIndex = -1,
            toIndex = -1,
            pointIndex = -1
        } = config ?? {};

        const link = this.editorStore.getLinks.find(p => p.id === linkId);
        if (!link) return;

        const isOceanLink = link.oceanLink;
        const isPortLoop = link.type === "PortLoop";

        const items = [
            isLink && ContextMenuItem('Insert point', () => this.insertPointToLink(linkId, fromIndex, toIndex, latLng)),
            isMidPoint && ContextMenuItem('Delete point', () => this.deletePointFromLink(linkId, pointIndex), true),
            ContextMenuItem(
                `${isOceanLink ? 'Reset' : 'Set'} Ocean Link (current: ${isOceanLink ? 'Yes' : 'No'})`, 
                () => this.toggleOceanLink(linkId)
            ),
            ContextMenuItem(
                `${isPortLoop ? 'Reset' : 'Set'} Port Loop (current: ${isPortLoop ? 'Yes' : 'No'})`, 
                () => this.toggleLinkPortLoop(linkId), 
                true
            ),       
            ContextMenuItem('Delete link', () => {
                if (confirm(`Delete this link ${link.id}?`))
                    this.deleteLink(link.id);
            }),
        ]

        const contextMenu = ContextMenu.contextMenuHandler({
            x,
            y,
            items
        });

        return contextMenu;
    }

    patchContextMenuHandler(patchId, latLng, x, y, config) {
        const {
            isPatch = false,
            isFirstSegment = false,
            isMidSegment = false,
            isLastSegment = false,
            isFirstPoint = false,
            isLastPoint = false,
            isMidPoint = false,
            isDecorator = false,
            fromIndex = -1,
            toIndex = -1,
            pointIndex = -1
        } = config ?? {};

        const patch = this.editorStore.getPatches.find(p => p.id === patchId);
        const currentRegion = patch.region || '';
        const isPort = patch.port === true;
        const currentGroup = patch.group || '';
        const currentVirtual = patch.isVirtual || false;
        const currentJunction = patch.isJunction || false; 
        const isSelected = this.editorStore.isAddedToExportSelection(patchId); 

        ContextMenu.closeContextMenu();

        const items = [
            ContextMenuItem(
                isSelected ? 'Deselect' : 'Select',
                () => {
                    if (isSelected) {
                        this.editorStore.removeFromExportSelection(patch.id);
                    } else {
                        this.editorStore.addToExportSelection(patch.id);
                    }

                    this.updatePatchLayers(patch);
                }, 
                true
            ),
            isPatch && ContextMenuItem(
                'Insert point', 
                () => this.insertPointToPatch(patch.id, fromIndex, toIndex, latLng)
            ),
            isMidPoint && ContextMenuItem(
                'Delete point', 
                () => this.deletePointFromPatch(patch.id, pointIndex),
                true
            ),
            isMidSegment && ContextMenuItem(
                'Split Patch',
                () => this.splitPatch(patch.id, fromIndex, toIndex)
            ),
            ContextMenuItem('Edit path', () => this.promptLineDetails(patch.id)),
            ContextMenuItem('Delete path', () => {
                if (confirm('Delete this path?'))
                    this.deletePatch(patch.id);
            }, true),
            ContextMenuItem(
                isPort ? 'Remove port attribute' : 'Set as port', 
                () => this.togglePatchPortAttribute(patch.id)
            ),
            ContextMenuItem(
                `Set region (current: ${currentRegion || 'none'})`, 
                () => this.promptPatchRegion(patch.id, currentRegion)
            ),
            ContextMenuItem(
                `Set group (current: ${currentGroup || 'none'})`, 
                () => this.promptPatchGroup(patch.id, currentGroup)
            ),
            ContextMenuItem(
                `${currentVirtual ? "Reset" : "Set"} virtual (current: ${currentVirtual ? "Yes" : "No"})`, 
                () => this.togglePatchVirtual(patch.id, currentVirtual)
            ),
            ContextMenuItem(
                `${currentJunction ? "Reset" : "Set"} junction (current: ${currentJunction ? "Yes" : "No"})`, 
                () => this.togglePatchJunction(patch.id, currentJunction),
                true
            ),            
            ContextMenuItem(
                `Show incoming links`, 
                () => eventBus.$emit('show-incoming-links-to-patches', patch.name)
            ),
            ContextMenuItem(
                `Show outgoing links`, 
                () => eventBus.$emit('show-outgoing-links-from-patches', patch.name)
            ),
            ContextMenuItem(
                `Show all links`, 
                () => eventBus.$emit('show-all-links-in-patches')
            ),
        ];

        const contextMenu = ContextMenu.contextMenuHandler({
            x,
            y,
            items
        });

        return contextMenu;
    }    
    
    areaContextMenuHandler(areaId, latLng, x, y, config) {
        //const {} = config || {};

        const items = [
            ContextMenuItem('Edit area', () => this.promptAreaDetails(areaId)),
        ];

        const contextMenu = ContextMenu.contextMenuHandler({
            x,
            y,
            items
        });

        return contextMenu;
    }

    renderArea(area) {
        if (!area || !area.points || area.points.length < 3) return;
        const offsetPoints = this.offsetPoints(area.points);
        offsetPoints.forEach(points => {
            const areaLatLngs = points.map(point => L.latLng(point.lat, point.lon));
            const polygon = L.polygon(areaLatLngs, {
                color: area.depth <= 3 ? 'red' : area.depth <= 5 ? 'coral' : 'orange',
                weight: 3,
                areaId: area.id
            }).addTo(this.editorAreasGroup);

            polygon.on('contextmenu', (e) => {
                const areaId = e.target.options.areaId;
                this.areaContextMenuHandler(
                    areaId, 
                    e.latlng,
                    e.originalEvent.clientX, 
                    e.originalEvent.clientY,
                    {}
                );
                L.DomEvent.stop(e);
            });            
        });
    }

    renderPatchDecorators(config) {
        const {
            ids = null
        } = config ?? {};

        if (!ids) {
            this.editorDecoratorsGroup.clearLayers();
        }
        else {
            const decoratorLayers = this.editorDecoratorsGroup.getLayers();
            decoratorLayers.forEach(layer => {
                if (layer.options && layer.options.patchId !== undefined ) {
                    if (ids.includes(layer.options.patchId)) {
                        this.editorDecoratorsGroup.removeLayer(layer);
                    }
                }
            })
        }

        let zoom = this.map.getZoom();
        console.log("Zoom level", zoom);
        if (zoom < 7) return;

        const bounds = this.map.getBounds();
        const zone = {
            north: bounds.getNorthEast().lat,
            south: bounds.getSouthWest().lat,
            west: bounds.getSouthWest().lng,
            east: bounds.getNorthEast().lng
        }
        console.log(zone);

        let counter = 0;
        const layers = this.editorPatchesGroup.getLayers();
        layers.forEach(layer => {
            if (layer.options && layer.options.patchId !== undefined ) {
                if (layer instanceof L.Polyline) {
                    const {
                        patchId,
                        isPort,
                        points,
                        contextMenuOptions
                    } = layer.options;

                    if (ids && !ids.includes(patchId)) return;

                    if (!points.some(p => GeoUtils.isPointInsideZone(p, [zone]))) return;

                    counter++;
                    const polyline = layer;
                    const isSelected = this.editorStore.isAddedToExportSelection(patchId);
                    const pathColor = isSelected ? 'lime' : isPort ? 'green' : 'black';

                    const decorator = L.polylineDecorator(polyline, {
                        patterns: [
                            {
                                offset: '50%',
                                symbol: L.Symbol.arrowHead({
                                    pixelSize: 12,
                                    polygon: true,
                                    pathOptions: {
                                        fillOpacity: 1,
                                        weight: 0,
                                        color: pathColor
                                    }
                                })
                            }
                        ]
                    }).addTo(this.editorDecoratorsGroup);

                    decorator.options.patchId = patchId;
                    decorator.on('contextmenu', (e) => {
                        this.patchContextMenuHandler(
                            patchId, 
                            e.latlng,
                            e.originalEvent.clientX, 
                            e.originalEvent.clientY,
                            { 
                                isDecorator: true,
                                isPatch: true,
                                ...contextMenuOptions
                            }
                        );
                        L.DomEvent.stop(e);
                    });
                }
            }
        });
        console.log("Rendered decorators", counter);
    }

    renderPatch(patch) {
        if (!patch || !patch.points || patch.points.length < 2) return;

        this.editorTempGroup.clearLayers();

        const offsetPoints = this.offsetPoints(patch.points);
        offsetPoints.forEach(points => {
            const isSelected = this.editorStore.isAddedToExportSelection(patch.id);
            const patchLatLngs = points.map(point => L.latLng(point.lat, point.lon));
            const pathColor = isSelected ? 'lime' : patch.port ? 'green' : 'black';
            const lengthNM = this.calculatePathLength(patchLatLngs);
            
            for (let i = 0; i < patchLatLngs.length - 1; i++) {
                const latLngs = [patchLatLngs[i], patchLatLngs[i + 1]];

                const contextMenuOptions = {
                    isFirstSegment: i == 0,
                    isMidSegment: i != 0 && i != patchLatLngs.length - 2,
                    isLastSegment: i == patchLatLngs.length - 2,
                    fromIndex: i,
                    toIndex: i + 1
                }
                
                const polyline = L.polyline(latLngs, {
                    color: pathColor,
                    weight: 3,
                    patchId: patch.id,
                    dashArray: patch.isVirtual ? '10, 10' : null,
                    fromIndex: i,
                    toIndex: i + 1,
                    contextMenuOptions: contextMenuOptions,
                    isPort: patch.port,
                    points: [points[i], points[i + 1]]
                }).addTo(this.editorPatchesGroup);

                
                /*
                const decorator = L.polylineDecorator(polyline, {
                    patterns: [
                        {
                            offset: '50%',
                            symbol: L.Symbol.arrowHead({
                                pixelSize: 12,
                                polygon: true,
                                pathOptions: {
                                    fillOpacity: 1,
                                    weight: 0,
                                    color: pathColor
                                }
                            })
                        }
                    ]
                }).addTo(this.editorDecoratorsGroup);

                decorator.options.patchId = patch.id;
                decorator.on('contextmenu', (e) => {
                    this.patchContextMenuHandler(
                        patch.id, 
                        e.latlng,
                        e.originalEvent.clientX, 
                        e.originalEvent.clientY,
                        { 
                            isDecorator: true,
                            isPatch: true,
                            ...contextMenuOptions
                        }
                    );
                    L.DomEvent.stop(e);
                });
                */

                polyline.on('contextmenu', (e) => {               
                    const patchId = e.target.options.patchId;
                    this.patchContextMenuHandler(
                        patchId, 
                        e.latlng,
                        e.originalEvent.clientX, 
                        e.originalEvent.clientY,
                        { 
                            isPatch: true,
                            ...contextMenuOptions,
                        }
                    );
                    L.DomEvent.stop(e);
                });

                polyline.on('mouseover', (e) => {
                    if (this.lengthDisplay) {
                        const portStatus = patch.port ? ' (Port)' : '';
                        const regionInfo = patch.region ? `, Region: ${patch.region}` : '';
                        this.lengthDisplay.innerHTML = `<span>Path Length: ${lengthNM} nm, ID: ${patch.id}${portStatus}${regionInfo}</span>`;
                        this.lengthDisplay.style.display = 'block';
                    }
                });

                polyline.on('mouseout', (e) => {
                    if (this.lengthDisplay) {
                        this.lengthDisplay.style.display = 'none';
                    }
                });

            }

            // add point markers
            this.addFromMarker(patchLatLngs[0], patch);

            for (let i = 1; i < patchLatLngs.length - 1; i++) {
                const pointMarker = L.circleMarker(patchLatLngs[i], {
                    radius: 4,
                    fillColor: "#000",
                    color: "#000",
                    weight: 1,
                    opacity: 1,
                    fillOpacity: 1,
                    patchId: patch.id,
                    pointIndex: i
                }).addTo(this.editorMarkersGroup);

                pointMarker.on('contextmenu', (e) => {
                    const patchId = e.target.options.patchId;
                    this.patchContextMenuHandler(
                        patchId, 
                        e.latlng,
                        e.originalEvent.clientX, 
                        e.originalEvent.clientY,
                        { isMidPoint: true, pointIndex: i }
                    );
                    L.DomEvent.stop(e);
                });

                this.addPatchPointMoveHandler(patch, pointMarker);
            }

            this.addToMarker(patchLatLngs[patchLatLngs.length - 1], patch, patchLatLngs.length - 1);
        });
    }
    
    patchPointMove = null;
    linkPointMove = null;

    addlinkPointMoveHandler(link, pointMarker) {
        pointMarker.on('mousedown', (e) => {
            this.map.dragging.disable();   
            this.linkPointMove = {
                link,
                linkId: e.target.options.linkId,
                pointIndex: e.target.options.pointIndex
            }
            L.DomEvent.stop(e);            
        })
        
        pointMarker.on('mouseup', (e) => {
            this.map.dragging.enable();
            this.linkPointMove = null;
            L.DomEvent.stop(e);            
        }) 
    }

    addPatchPointMoveHandler(patch, pointMarker) {
        pointMarker.on('mousedown', (e) => {
            this.map.dragging.disable();   
            this.patchPointMove = {
                patch,
                patchId: e.target.options.patchId,
                pointIndex: e.target.options.pointIndex
            }
            L.DomEvent.stop(e);            
        })
        
        pointMarker.on('mouseup', (e) => {
            this.map.dragging.enable();
            this.patchPointMove = null;
            L.DomEvent.stop(e);            
        }) 
    }

    promptPatchRegion(patchId, currentRegion = '') {
        const newRegion = prompt('Enter region name:', currentRegion);

        if (newRegion !== null) {
            this.updatePatchRegion(patchId, newRegion);
        }
    }

    promptPatchGroup(patchId, currentGroup = '') {
        const newGroup = prompt('Enter group name:', currentGroup);

        if (newGroup !== null) {
            this.updatePatchGroup(patchId, newGroup);
        }
    }

    togglePatchVirtual(patchId, currentVirtual) {
        this.updatePatchVirtual(patchId, !currentVirtual);
        this.updatePatchStyle(patchId);
    }

    togglePatchJunction(patchId, currentJunction)
    {
        this.updatePatchJunction(patchId, !currentJunction);
        this.updatePatchStyle(patchId);
    }

    updatePatchRegion(patchId, region) {
        this.editorStore.updatePatchRegion(patchId, region);
    }

    updatePatchGroup(patchId, group) {
        this.editorStore.updatePatchGroup(patchId, group);
    }

    updatePatchVirtual(patchId, isVirtual) {
        this.editorStore.updatePatchVirtual(patchId, isVirtual);
    }

    updatePatchJunction(patchId, isJunction) {
        this.editorStore.updatePatchJunction(patchId, isJunction);
    }

    addDecorators(marker, patchId) {
        const isSelected = marker.options.isSelected;

        marker.setStyle({
            fillColor: isSelected ? "#00ff00" : "#ff0b0b"
        });

        this.editorPatchesGroup.eachLayer(layer => {
            if (layer.options && layer.options.patchId === patchId) {
                if (layer instanceof L.Polyline) {
                    layer.setStyle({
                        color: isSelected ? 'green' : 'black'
                    });
                }
            }
        });

        let decoratorsToRemove = [];
        this.editorDecoratorsGroup.eachLayer(layer => {

            if (layer.options &&
                layer.options.patchId === patchId &&
                layer.options.patterns &&
                layer.options.patterns[0] &&
                layer.options.patterns[0].symbol) {
                decoratorsToRemove.push(layer);
            }
        });

        decoratorsToRemove.forEach(decorator => {
            this.editorDecoratorsGroup.removeLayer(decorator);
        });

        let targetPolylines = [];
        this.editorPatchesGroup.eachLayer(layer => {
            if (layer.options &&
                layer.options.patchId === patchId &&
                layer instanceof L.Polyline &&
                !layer.options.patterns) {
                targetPolylines.push(layer);
            }
        });

        targetPolylines.forEach(targetPolyline => {
            const decorator = L.polylineDecorator(targetPolyline, {
                patterns: [
                    {
                        offset: '50%',
                        symbol: L.Symbol.arrowHead({
                            pixelSize: 12,
                            polygon: true,
                            pathOptions: {
                                fillOpacity: 1,
                                weight: 0,
                                color: isSelected ? 'green' : 'black'
                            }
                        })
                    }
                ]
            }).addTo(this.editorDecoratorsGroup);
            decorator.options.patchId = patchId;

            decorator.on('contextmenu', (e) => {
                this.patchContextMenuHandler(
                    patchId, 
                    e.latlng,
                    e.originalEvent.clientX, 
                    e.originalEvent.clientY,
                    { isDecorator: true }
                );
                L.DomEvent.stop(e);
            });
        })
    }

    addFromMarker(position, patch) {
        const marker = L.circleMarker(position, {
            radius: 4,
            fillColor: "#ff0b0b",
            color: "#000000",
            weight: 1,
            opacity: 1,
            fillOpacity: 1,
            patchId: patch.id,
            pointIndex: 0,
            isSelected: false
        }).addTo(this.editorMarkersGroup);

        marker.on('contextmenu', (e) => {
            const patchId = e.target.options.patchId;
            this.patchContextMenuHandler(
                patchId, 
                e.latlng,
                e.originalEvent.clientX, 
                e.originalEvent.clientY,
                { isFirstPoint: true }
            );
            L.DomEvent.stop(e);
        });

        this.addPatchPointMoveHandler(patch, marker);

        return marker;
    }

    addToMarker(position, patch, pointIndex) {
        const marker = L.circleMarker(position, {
            radius: 4,
            fillColor: "#000000",
            color: "#000000",
            weight: 1,
            opacity: 1,
            fillOpacity: 1,
            patchId: patch.id,
            pointIndex: pointIndex
        }).addTo(this.editorMarkersGroup);

        marker.on('contextmenu', (e) => {
            const patchId = e.target.options.patchId;
            this.patchContextMenuHandler(
                patchId,
                e.latlng, 
                e.originalEvent.clientX, 
                e.originalEvent.clientY,
                { isLastPoint: true }
            );
            L.DomEvent.stop(e);
        });        

        this.addPatchPointMoveHandler(patch, marker);

        return marker;
    }

    deletePatch(patchId) {
        const patch = this.editorStore.getPatches.find(patch => patch.id == patchId);
        if (!patch) return;

        this.removePatchLayers(patch);

        const patches = this.editorStore.getPatches.filter(patch => patch.id !== patchId);
        this.editorStore.$patch({patches});

        this.undoAction(
            () => {
                this.editorStore.addPatch(patch);
                const storePatch = this.editorStore.getPatches.find(l => l.id === patch.id);
                this.updatePatchLayers(storePatch);            
            }
        );
    }

    deleteArea(areaId) {
        const area = this.editorStore.getAreas.find(area => area.id == areaId);
        if (!area) return;

        this.removeAreaLayers(area);

        const areas = this.editorStore.getAreas.filter(area => area.id !== areaId);
        this.editorStore.$patch({areas});

        this.undoAction(
            () => {
                this.editorStore.addArea(area);
                const storeArea = this.editorStore.getAreas.find(l => l.id === area.id);
                this.updateAreaLayers(storeArea);            
            }
        );
    }

    loadData() {
        this.clearData();
        this.loadDataAsync();
    }

    async loadDataAsync() {
        eventBus.$emit('editor-data-load-status', true);
        await this.loadPatchesAsync();
        await this.loadLinksAsync();
        this.loadAreas();
        eventBus.$emit('editor-data-load-status', false);
        console.log("loadDataAsync completed");
    }

    clearLinks() {
        this.editorLinksGroup.clearLayers();
    }
    
    clearData() {
        this.editorTempGroup.clearLayers();
        this.editorPatchesGroup.clearLayers();
        this.editorDecoratorsGroup.clearLayers();
        this.editorMarkersGroup.clearLayers();
        this.editorAreasGroup.clearLayers();
        this.clearLinks();
    }

    checkLineClick(e) {

    }
}
